import { GetStaticProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import React from 'react';
import { useLocation } from 'react-use';

import Banner from '@/components/Home/Banner';
import Blog from '@/components/Home/Blog';
import CourseIntroduction from '@/components/Home/CourseIntroduction';
import Honor from '@/components/Home/Honor';
import OnlineClass from '@/components/Home/OnlineClass';
import Teachers from '@/components/Home/Teachers';
import TeachingResearch from '@/components/Home/TeachingResearch';
import UserReviews from '@/components/Home/UserReviews';
import Layout from '@/components/Layout';
// import { BLOG_URL } from '@/utils/constants';

// import { useLenis } from '@studio-freight/react-lenis';

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => {
  // 获取多语言翻译
  const translations = await serverSideTranslations(locale, [
    'common',
    'home',
    'path',
    'form',
  ]);
  return {
    props: {
      ...translations,
    },
  };
};

const Home = () => {
  const state = useLocation();
  const { t, i18n } = useTranslation(['home', 'common']);

  // const lenis = useLenis(({ scroll }) => {
  //   console.log('scroll', scroll);
  //   // called every scroll
  // });

  // useLenis((lenis) => {
  //   console.log('Current page progress', lenis.scroll, lenis.limit);
  // });

  return (
    <>
      <NextSeo
        openGraph={{
          url: state?.href,
          title: t('common:seo-首页-title'),
          description: t('common:seo-首页-description'),
          images: [
            {
              url: 'https://cdnwukong.com/images/public/og/offical-site-1.png',
              width: 300,
              height: 300,
              alt: t('common:seo-首页-title'),
            },
          ],
        }}
      />
      <Head>
        <title>{t('common:seo-首页-title')}</title>
        <meta name="description" content={t('common:seo-首页-description')} />
        <meta name="keywords" content={t('common:seo-首页-关键词')} />
        <meta
          data-n-head="ssr"
          data-hid="apple-mobile-web-app-title"
          name="apple-mobile-web-app-title"
          content={t('common:seo-首页-title')}
        />
        <script
          type="text/javascript"
          src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
          async
        />
        {/* 预加载LCP背景图片 */}
        <link
          rel="preload"
          href="/images/home/bg.svg"
          as="image"
          media="(min-width: 768px)"
        />
        <link
          rel="preload"
          href="/images/home/bg-m.svg"
          as="image"
          media="(max-width: 767px)"
        />
        {/* SEO 仅首页 开始 */}
        {/* <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'Organization',
              title: t('common:seo-首页-title'),
              url: 'https://www.wukongsch.com',
              logo: 'https://cdnwukong.com/images/public/og/offical-site-1.png',
              brand: {
                '@type': 'Brand',
                name: 'WuKong Education',
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'Product',
              name: t('common:seo-首页-title'),
              description: t('common:seo-首页-description'),
              url: 'https://www.wukongsch.com',
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: '4.7',
                ratingCount: '36805',
                reviewCount: '1529',
              },
              brand: {
                '@type': 'Brand',
                name: 'WuKong Education',
              },
              operatingSystem: 'WINDOWS, MAC, iOS, ANDROID',
              offers: {
                '@type': 'AggregateOffer',
                offerCount: '999999999',
                lowPrice: '0.00',
                highPrice: '0.00',
                priceCurrency: 'USD',
              },
            }),
          }}
        /> */}
        {i18n.language === 'en' ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'Organization',
                    name: 'WuKong Education',
                    url: 'https://www.wukongsch.com/',
                    logo: 'https://cdnwukong.com/images/public/og/offical-site-1.png',
                    hasCredential: [
                      {
                        '@type': 'EducationalOccupationalCredential',
                        name: 'kidSAFE Seal Program',
                        url: 'https://www.kidsafeseal.com/certifiedproducts/wukong.html',
                      },
                      {
                        '@type': 'EducationalOccupationalCredential',
                        name: '2024 Cognia® Schools or Systems of Distinction',
                        url: 'https://www.cognia.org/schools-of-distinction/#:~:text=United%20Arab%20Emirates-,WuKong%20Education,-California%2C%20USA',
                      },
                    ],
                  },
                  {
                    '@type': 'Service',
                    name: 'WuKong Education - Online classes for 3 to 18-year-old students', //取页面的【title】，下面的ZH同
                    description:
                      'WuKong Education delivers trusted online learning for ages 3–18, featuring Chinese, Math, and English ELA programs and recognized with a 395/400 Cognia accreditation score.', //取页面的【Meta description】,下面的ZH同
                    url: 'https://www.wukongsch.com/',
                    serviceType: 'Online 1-on-1 tutoring',
                    audience: {
                      '@type': 'Audience',
                      audienceType: ['Students', 'Parents'],
                    },
                    availableChannel: {
                      '@type': 'ServiceChannel',
                      name: 'web site',
                      availableLanguage: ['English', 'Chinese'],
                      serviceUrl:
                        'https://www.wukongsch.com/independent-appointment/',
                    },
                    aggregateRating: {
                      '@type': 'AggregateRating',
                      ratingValue: '4.7',
                      ratingCount: '36805',
                      reviewCount: '1529',
                    },
                    potentialAction: {
                      '@type': 'ReserveAction',
                      name: 'Free Trial Class',
                      target: {
                        '@type': 'EntryPoint',
                        urlTemplate:
                          'https://www.wukongsch.com/independent-appointment/',
                        actionPlatform: [
                          'https://schema.org/DesktopWebPlatform',
                          'https://schema.org/MobileWebPlatform',
                        ],
                      },
                      description:
                        "Fill out the form to book a free trial class with WuKong Education. Choose subjects, share your expectations, and help us personalize your child's learning experience.", //取https://www.wukongsch.com/independent-appointment/页面的【meta description】,下面的ZH同（取https://www.wukongsch.com/zh/independent-appointment/）
                    },
                    offers: {
                      '@type': 'Offer',
                      price: '0',
                      priceCurrency: 'USD',
                      availability: 'https://schema.org/InStock',
                    },
                    provider: {
                      '@type': 'Organization',
                      name: 'WuKong Education',
                    },
                  },
                ],
              }),
            }}
          />
        ) : (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'Organization',
                    name: '悟空教育',
                    url: 'https://www.wukongsch.com/',
                    logo: 'https://cdnwukong.com/images/public/og/offical-site-1.png',
                    hasCredential: [
                      {
                        '@type': 'EducationalOccupationalCredential',
                        name: 'kidSAFE认证',
                        url: 'https://www.kidsafeseal.com/certifiedproducts/wukong.html',
                      },
                      {
                        '@type': 'EducationalOccupationalCredential',
                        name: 'Cognia®官方认证',
                        url: 'https://www.cognia.org/schools-of-distinction/#:~:text=United%20Arab%20Emirates-,WuKong%20Education,-California%2C%20USA',
                      },
                    ],
                  },
                  {
                    '@type': 'Service',
                    name: '悟空教育 - 为全球3-18岁学生提供中文、数学、英语在线直播课程',
                    description:
                      '悟空教育（WuKong Education）为3-18岁学生提供专业中文、国际数学与英语阅读写作的在线直播课程，荣获Cognia认证高分395/400，值得信赖的全球在线教育品牌。',
                    url: 'https://www.wukongsch.com/zh/',
                    serviceType: '在线一对一辅导',
                    audience: {
                      '@type': 'Audience',
                      audienceType: ['学生', '家长'],
                    },
                    availableChannel: {
                      '@type': 'ServiceChannel',
                      name: '网站',
                      availableLanguage: ['英语', '中文'],
                      serviceUrl:
                        'https://www.wukongsch.com/zh/independent-appointment/',
                    },
                    aggregateRating: {
                      '@type': 'AggregateRating',
                      ratingValue: '4.7',
                      ratingCount: '36805',
                      reviewCount: '1529',
                    },
                    potentialAction: {
                      '@type': 'ReserveAction',
                      name: '0元试听课',
                      target: {
                        '@type': 'EntryPoint',
                        urlTemplate:
                          'https://www.wukongsch.com/zh/independent-appointment/',
                        actionPlatform: [
                          'https://schema.org/DesktopWebPlatform',
                          'https://schema.org/MobileWebPlatform',
                        ],
                      },
                      description:
                        '填写表单并预约悟空教育的免费试听课。选择您想试听的科目，提交您的学习期望，让孩子获得更个性化的课程体验。',
                    },
                    offers: {
                      '@type': 'Offer',
                      price: '0',
                      priceCurrency: 'USD',
                      availability: 'https://schema.org/InStock',
                    },
                    provider: {
                      '@type': 'Organization',
                      name: '悟空教育',
                    },
                  },
                ],
              }),
            }}
          />
        )}
        {/* SEO 仅首页 结束 */}
      </Head>
      <main className="relative overflow-hidden">
        {/* pc 拆减两边 移动端铺满 */}
        <div className="absolute left-1/2 top-0 h-[758px] w-full translate-x-[-50%] md:h-[5340px] md:w-[1920px]">
          <Image
            src="/images/home/bg-m.svg"
            className="block h-full w-full object-cover md:hidden"
            alt="背景图片"
            fetchPriority="high"
            width={1920}
            height={758}
            loading="eager"
          />
          <Image
            src="/images/home/bg.svg"
            className="hidden h-full w-full object-cover md:block"
            alt="背景图片"
            fetchPriority="high"
            width={1920}
            height={5340}
            loading="eager"
          />
        </div>
        <div className="relative z-10 bg-transparent">
          <Banner />
          {/* 线上课堂 */}
          <OnlineClass />
          {/* 课程介绍 */}
          <CourseIntroduction />
          {/* 师资 */}
          <Teachers />
          {/* 用户评价 */}
          <UserReviews />
          {/* 教学研发 */}
          <TeachingResearch />
          {/* 荣誉 */}
          <Honor />
          {/* 博客 */}
          <Blog exposeEvent="O_Home_BlogExpose" />
        </div>
      </main>
    </>
  );
};

export default Home;

Home.getLayout = (page: React.ReactElement) => (
  <Layout tooterTheme="home">{page}</Layout>
);
